import { geohashQueryBounds, distanceBetween } from 'geofire-common';

// Proximity search for the homepage.
//
// The listings query is capped (MAX_LISTINGS in HomePage) and ordered by the
// admin's sortOrder, which is fine as a default view but is the wrong set to
// answer "what is near me" from: past that cap, a visitor's nearest restaurants
// may simply not be among the ones loaded, and sorting by distance would rank a
// slice of the catalogue that never included them. That failure is silent —
// the results look plausible, they are just not the closest.
//
// A geohash prefix range is a single-field inequality, which is the only shape
// of proximity query Firestore can run. geohashQueryBounds turns a centre and a
// radius into a handful of such ranges; each is one query, and the union covers
// the circle with some slop that the true-distance filter below removes.

// Radii tried in order, stopping as soon as enough restaurants are found.
// Starting small keeps the common case — a city with plenty nearby — to the
// cheapest possible read, while the last step is wide enough that somewhere
// genuinely remote still gets an answer rather than an empty list.
export const NEARBY_RADII_M = [25000, 100000, 500000, 2000000];

// Enough results to fill a page or two and give the distance sort something to
// work with. Not a hard cap on what is displayed — it decides when to stop
// widening the search.
export const NEARBY_TARGET_COUNT = 60;

// Per-range cap, so one dense range cannot dominate the union.
export const NEARBY_PER_RANGE_LIMIT = 40;

// The geohash ranges covering `radiusM` around a point. Exposed so the caller
// can turn each into a Firestore query without importing geofire itself.
export function nearbyBounds(coords, radiusM) {
  return geohashQueryBounds([coords.lat, coords.lng], radiusM);
}

// Metres between two {lat,lng} points.
export function distanceMetres(a, b) {
  return distanceBetween([a.lat, a.lng], [b.lat, b.lng]) * 1000;
}

// Merge restaurants from several sources into one list, keeping the first copy
// of any id.
//
// The nearby queries overlap each other at range boundaries, and they also
// overlap the default listings already loaded — so the same restaurant arrives
// more than once and would otherwise render as a duplicate card.
export function mergeById(...lists) {
  const byId = new Map();
  for (const list of lists) {
    for (const item of list || []) {
      if (item && item.id && !byId.has(item.id)) byId.set(item.id, item);
    }
  }
  return [...byId.values()];
}

// Keep only what is genuinely within the radius, closest first.
//
// The geohash ranges are a rectangular over-approximation of a circle, so they
// return corners that are further away than asked for. Without this a "within
// 25km" search would include points at 35km purely because of how the grid
// falls, and they would sort ahead of nothing in particular.
export function withinRadius(restaurants, coords, radiusM, coordsFor) {
  return restaurants
    .map((restaurant) => {
      const point = coordsFor(restaurant);
      return point ? { restaurant, dist: distanceMetres(coords, point) } : null;
    })
    .filter((entry) => entry && entry.dist <= radiusM)
    .sort((a, b) => a.dist - b.dist)
    .map(({ restaurant, dist }) => ({ ...restaurant, dist }));
}

// Which radius to stop at, given how many results each one found. Pure, so the
// widening policy can be tested without touching Firestore.
export function chooseRadius(countsByRadius, target = NEARBY_TARGET_COUNT) {
  for (const { radiusM, count } of countsByRadius) {
    if (count >= target) return radiusM;
  }
  return countsByRadius.length ? countsByRadius[countsByRadius.length - 1].radiusM : null;
}
